import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Heart, Radio, Video, Loader2 } from "lucide-react";
import { LiveStage } from "../components/live/LiveStage";
import { InviteParty } from "../components/live/InviteParty";
import { RemindMe } from "../components/live/LiveHighlights";
import { uploadChunked } from "../lib/stageRecorder";
import { toast } from "sonner";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../lib/auth";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { StoryBubbles, LiveMoments } from "../components/social/StoryBubbles";

export function LiveIndex() {
  const [data, setData] = useState({ upcoming: [], past: [] });
  useEffect(() => {
    api.get("/broadcasts").then((r) => setData(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-[var(--bg)]">
      <Navbar />
      <section className="section-pad bg-[var(--bg-warm)]">
        <div className="container-wide px-6 md:px-12 text-center">
          <Radio className="w-8 h-8 mx-auto text-[var(--gold)] mb-4 animate-pulse" />
          <p className="font-script text-[var(--gold)] text-4xl mb-2">Locked In Live</p>
          <h1 className="font-serif font-light text-5xl md:text-7xl">Wedding-day broadcasts</h1>
          <p className="text-[var(--muted)] mt-4 max-w-xl mx-auto">Watch couples say "I do" in real time. No app, no signup — just love, streamed.</p>
        </div>
      </section>
      <section className="bg-black py-4" data-testid="live-stories-band"><div className="container-wide"><StoryBubbles /></div></section>
      <section className="section-pad">
        <div className="container-wide px-6 md:px-12">
          <p className="font-script text-[var(--gold)] text-3xl mb-2">Upcoming & Live</p>
          {data.upcoming.length === 0 && <p className="text-center text-[var(--muted)] py-12">No upcoming broadcasts. Be the first to go live.</p>}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
            {data.upcoming.map((b) => (
              <Link key={b.broadcast_id} to={`/live/${b.broadcast_id}`} data-testid={`live-card-${b.broadcast_id}`} className="bg-white border border-[var(--line)] rounded-2xl overflow-hidden hover:border-[var(--gold)] transition">
                <div className="aspect-video bg-[var(--bg-warm)] relative">
                  {b.cover_image && <img src={b.cover_image.startsWith("http") ? b.cover_image : `${BACKEND_URL}/api/files/${b.cover_image}`} alt="" className="w-full h-full object-cover" />}
                  {b.is_live && <span className="absolute top-3 left-3 bg-red-600 text-white text-[10px] uppercase tracking-wider px-2 py-1 rounded-full flex items-center gap-1"><Radio className="w-3 h-3" /> Live now</span>}
                </div>
                <div className="p-5">
                  <p className="text-[10px] uppercase tracking-wider text-[var(--gold)] mb-1">{new Date(b.scheduled_at).toLocaleString()}</p>
                  <h3 className="font-serif text-xl">{b.title}</h3>
                  {b.couple_names && <p className="font-script text-2xl text-[var(--gold)] mt-1">{b.couple_names}</p>}
                  {b.description && <p className="text-sm text-[var(--muted)] mt-2 line-clamp-2">{b.description}</p>}
                </div>
              </Link>
            ))}
          </div>

          {data.past.length > 0 && (
            <>
              <p className="font-script text-[var(--gold)] text-3xl mt-16 mb-2">Past broadcasts</p>
              <div className="grid md:grid-cols-3 gap-4 mt-4">
                {data.past.map((b) => (
                  <div key={b.broadcast_id} className="bg-[var(--bg-warm)] rounded-xl p-5 opacity-80">
                    <p className="font-serif text-lg">{b.title}</p>
                    <p className="text-xs text-[var(--muted)]">{new Date(b.scheduled_at).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
}

function LiveGate({ b, user, isHost, onJoin, busy, error }) {
  const [name, setName] = useState("");
  const ended = b.is_ended;
  const waiting = !b.is_live && !isHost;
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6" data-testid="live-gate">
      <div className="text-center max-w-sm w-full">
        <Radio className={`w-10 h-10 mx-auto mb-4 ${b.is_live ? "text-red-400 animate-pulse" : "text-[var(--gold)]"}`} />
        <p className="font-serif text-2xl mb-1">{b.title}</p>
        <p className="text-sm text-white/60 mb-6" data-testid="live-gate-status">
          {ended ? "This broadcast has ended. Thank you for watching." : waiting ? `Starts ${b.scheduled_at ? new Date(b.scheduled_at).toLocaleString() : "soon"}. This page will update when they go live.` : isHost ? "You're the host. Go live when you're ready." : "They're live right now."}
        </p>
        {!ended && !waiting && (
          <>
            {!user && !isHost && (
              <input data-testid="live-guest-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" className="w-full bg-white/10 rounded-full px-5 py-3 text-sm outline-none placeholder-white/40 mb-3 text-center" />
            )}
            <button data-testid="live-join-btn" disabled={busy || (!user && !isHost && !name.trim())} onClick={() => onJoin(name.trim())} className="btn-gold w-full justify-center disabled:opacity-50">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Video className="w-4 h-4" />}
              {isHost ? (b.is_live ? "Rejoin as host" : "Go live now") : "Watch live"}
            </button>
          </>
        )}
        {ended && <Link to={`/live/${b.broadcast_id}/highlights`} className="btn-gold w-full justify-center" data-testid="live-highlights-link">Watch the highlights</Link>}
        {waiting && !ended && <RemindMe bid={b.broadcast_id} />}
        {error && <p className="text-sm text-red-300 mt-4" data-testid="live-join-error">{error}</p>}
      </div>
    </div>
  );
}

export function LiveRoom() {
  const { id } = useParams();
  const { user } = useAuth();
  const [b, setB] = useState(null);
  const [join, setJoin] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sp = new URLSearchParams(window.location.search);
  const hostKey = sp.get("key");
  const stageKey = sp.get("stage") || "";
  const isHost = !!(b && (hostKey || (user && (b.host_user_id === user.user_id || user.role === "admin"))));

  useEffect(() => {
    let stop = false;
    const load = () => api.get(`/broadcasts/${id}`).then((r) => !stop && setB(r.data)).catch(() => !stop && setError("Broadcast not found."));
    load();
    const t = setInterval(() => { if (!stop) load(); }, 10000);
    return () => { stop = true; clearInterval(t); };
  }, [id]);

  async function enter(name) {
    setBusy(true); setError("");
    try {
      const { data } = hostKey
        ? await api.post(`/public/broadcasts/${id}/host`, { host_key: hostKey })
        : user
          ? await api.post(`/broadcasts/${id}/join`, { stage_key: stageKey })
          : await api.post(`/broadcasts/${id}/watch`, { name, stage_key: stageKey });
      if (data.host) setB((prev) => ({ ...prev, is_live: true }));
      setJoin(data);
    } catch (e) { setError(formatApiError(e)); } finally { setBusy(false); }
  }

  async function saveReplay(blob) {
    if (!blob || blob.size < 1000) return;
    if (!hostKey) {
      await toast.promise(uploadChunked(`/broadcasts/${id}/replay-chunk`, blob, api), { loading: "Saving your live replay…", success: "Replay saved. Highlights are ready on the live page.", error: (e) => formatApiError(e) });
      return;
    }
    const fd = new FormData();
    fd.append("file", blob, `replay.${blob.type.includes("mp4") ? "mp4" : "webm"}`);
    fd.append("host_key", hostKey);
    await toast.promise(api.post(`/public/broadcasts/${id}/replay`, fd), {
      loading: "Saving your live replay to the wedding feed…",
      success: (r) => (r.data.approved ? "Replay posted to the wedding feed!" : "Replay saved. It shows once the couple approves it."),
      error: (e) => formatApiError(e),
    });
  }

  async function endBroadcast() {
    try {
      if (hostKey) await api.post(`/public/broadcasts/${id}/end`, { host_key: hostKey });
      else await api.patch(`/broadcasts/${id}`, { is_ended: true });
      toast.success("Broadcast ended");
    } catch (e) { toast.error(formatApiError(e)); }
    setJoin(null);
    api.get(`/broadcasts/${id}`).then((r) => setB(r.data)).catch(() => {});
  }

  if (!b) return <div className="min-h-screen bg-[var(--ink)] text-white/60 flex items-center justify-center text-sm">{error || "Loading…"}</div>;
  if (join) {
    return (
      <div className="fixed inset-0 bg-black z-50" data-testid="live-room">
        <LiveStage join={join} title={b.couple_names || b.title} vertical onLeave={() => setJoin(null)} onEnd={join.host ? endBroadcast : undefined}
          onRecorded={join.host && b.wedding_id ? saveReplay : undefined} autoRecord={!!(join.host && b.wedding_id)} bid={id} slug={b.wedding_slug || ""}
          hostTools={<InviteParty bid={id} hostKey={hostKey || ""} />} />
      </div>
    );
  }
  return (
    <div data-testid="live-room" className="min-h-screen bg-[var(--ink)] text-white flex flex-col">
      <div className="px-4 md:px-6 py-3 flex items-center gap-3 border-b border-white/10">
        <Link to="/live" className="text-sm text-white/70" data-testid="live-back">← Back</Link>
        <div className="flex-1 text-center min-w-0">
          <p className="font-script text-[var(--gold)] text-2xl leading-none truncate">{b.couple_names || b.title}</p>
          <p className="text-xs text-white/50">{new Date(b.scheduled_at || b.live_started_at || b.created_at).toLocaleString()} {b.is_live && <span className="ml-2 inline-flex items-center gap-1 text-red-400" data-testid="live-badge"><Radio className="w-3 h-3 animate-pulse" /> Live</span>}</p>
        </div>
        <Heart className="w-5 h-5 text-[var(--gold)]" />
      </div>
      {b.wedding_id && <LiveMoments id={id} />}
      <div className="relative flex-1 bg-black min-h-[70vh]">
        {stageKey && <p className="absolute top-4 inset-x-0 text-center text-xs text-[var(--gold)] z-10" data-testid="live-stage-invite-note">You've been invited to join on camera</p>}
        <LiveGate b={b} user={user} isHost={isHost} onJoin={enter} busy={busy} error={error} />
      </div>
    </div>
  );
}
